<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import DaIcon from '@/components/elements/daIcon/DaIcon.vue';
import DaModalFrame from '@/components/common/daModalFrame/DaModalFrame.vue';
import Loading from '@/components/common/Loading.vue';
import { $api } from '@/services';
import { _ROUTER_NAME } from '@/enums';
import ConfirmSend from '@/views/planPreview/ConfirmSend.vue';
import CreateNewForm from '@/views/record/studentDetail/features/result/CreateNewForm.vue';
import ScreeningCreate from '@/views/screening/features/ScreeningCreate.vue';
import ScreeningFilter from '@/views/screening/features/ScreeningFilter.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const filterOpen = ref(false);
const createOpen = ref(false);
const studentId = ref('');
const stateFilter = ref('');
const listLoading = ref(false);
const items = ref([]);
const activeTab = ref(
	route.name === _ROUTER_NAME.PROCESS_ASSESSMENT ? 'summary' : 'created'
);
const sendOpen = ref(false);
const pendingSend = ref(null);
const formOpen = ref(false);
const formStudent = ref(null);

const processTabs = [
	{
		id: 'summary',
		label: 'Tổng hợp kết quả can thiệp',
		usedAi: true,
	},
	{
		id: 'evaluation',
		label: 'Đánh giá kết quả can thiệp',
		usedAi: false,
	},
];

const developmentTabs = [
	{ id: 'created', label: 'Chưa hoàn thành', state: 'created' },
	{ id: 'completed', label: 'Hoàn thành', state: 'completed' },
];

const isProcess = computed(
	() => route.name === _ROUTER_NAME.PROCESS_ASSESSMENT
);

const isDevelopment = computed(
	() => route.name === _ROUTER_NAME.DEVELOPMENT_ASSESSMENT
);

const isList = computed(() => isProcess.value || isDevelopment.value);

const pageTabs = computed(() =>
	isDevelopment.value ? developmentTabs : processTabs
);

const activeUsedAi = computed(
	() => processTabs.find((tab) => tab.id === activeTab.value)?.usedAi === true
);

const visibleItems = computed(() =>
	items.value.filter((item) => {
		if (
			studentId.value &&
			String(item.student?.id) !== String(studentId.value)
		) {
			return false;
		}
		if (!isProcess.value) return true;
		if (stateFilter.value === 'sent' && !item.sent_at) return false;
		if (stateFilter.value === 'unsend' && item.sent_at) return false;
		if (stateFilter.value === 'draft') return false;
		return true;
	})
);

const applyFilter = (payload) => {
	studentId.value = payload?.student_id || '';
	stateFilter.value = payload?.state || '';
	filterOpen.value = false;
};

const openCreate = () => {
	if (!isList.value) return;
	createOpen.value = true;
};

const continueProcess = ({ student }) => {
	if (!student?.id) return;
	createOpen.value = false;
	if (isDevelopment.value) {
		formStudent.value = student;
		formOpen.value = true;
		return;
	}
	router.push({
		name: _ROUTER_NAME.RECORD_STUDENT_DETAIL_PLAN,
		params: { id: student.id },
	});
};

const formatDate = (value) => {
	if (!value) return '';
	const [year, month, day] = String(value).slice(0, 10).split('-');
	if (!day || !month || !year) return value;
	return `${day}/${month}/${year}`;
};

const conclusionLines = (item) => {
	if (Array.isArray(item?.conclusions)) {
		return item.conclusions.filter(Boolean);
	}
	if (item?.conclusion) return [item.conclusion];
	return [];
};

const fetchList = async () => {
	if (!isList.value) return;
	listLoading.value = true;
	const collected = [];
	try {
		let page = 1;
		const pageSize = 100;
		while (page < 20) {
			const res = isDevelopment.value
				? await $api.form.listStaffForms({
						state:
							activeTab.value === 'completed'
								? 'completed'
								: 'created',
						page,
						page_size: pageSize,
				  })
				: await $api.monthlyReport.listPeriodReports({
						used_ai: activeUsedAi.value,
						page,
						page_size: pageSize,
				  });
			const batch = res?.data?.data || [];
			collected.push(...batch);
			if (batch.length < pageSize) break;
			page += 1;
		}
		items.value = collected;
	} catch (err) {
		items.value = [];
		await store.dispatch('setAlertMessage', {
			message:
				err?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	} finally {
		listLoading.value = false;
	}
};

const selectTab = (id) => {
	if (activeTab.value === id) return;
	activeTab.value = id;
};

const openReport = (item) => {
	if (!item?.student?.id || !item?.id) return;
	if (isDevelopment.value) {
		router.push({
			path: `/record/student/${item.student.id}/result/${item.id}`,
			query: {
				student_form: 'true',
				formId: item.id,
			},
		});
		return;
	}
	router.push({
		path: `/record/student/${item.student.id}/result/${item.id}`,
	});
};

const showDash = (value) => {
	if (value === null || value === undefined || value === '') return '-';
	return value;
};

const askSend = (item) => {
	pendingSend.value = item;
	sendOpen.value = true;
};

const sendReport = async () => {
	const item = pendingSend.value;
	if (!item?.student?.id || !item?.id) return;
	try {
		const res = await $api.staff.sendAssessmentDocument(
			item.student.id,
			item.id
		);
		if (res) {
			await store.dispatch('setAlertMessage', {
				message: 'Gửi tới phụ huynh thành công',
				type: 'success',
			});
			await fetchList();
		}
		sendOpen.value = false;
	} catch (err) {
		await store.dispatch('setAlertMessage', {
			message: 'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
};

watch(activeTab, () => {
	if (isList.value) fetchList();
});

watch(
	() => route.name,
	(name) => {
		const next =
			name === _ROUTER_NAME.PROCESS_ASSESSMENT ? 'summary' : 'created';
		if (activeTab.value === next) fetchList();
		else activeTab.value = next;
	}
);

onMounted(() => {
	if (isList.value) fetchList();
});
</script>

<template>
	<div class="assessment-hub px-12 md-down:px-4 pt-12 pb-16">
		<div class="flex flex-wrap items-center justify-between gap-4">
			<div class="flex items-center gap-3">
				<DaIcon
					name="das--chart-book"
					class="assessment-hub__icon"
				/>
				<h1 class="assessment-hub__title">
					{{ route.meta.sectionTitle }}
				</h1>
			</div>
			<div class="flex items-center gap-6">
				<button
					type="button"
					class="button-p btn--ghost w-fit px-5"
					@click="openCreate"
				>
					Tạo mới
				</button>
				<button
					type="button"
					class="assessment-hub__filter"
					@click="filterOpen = true"
				>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M1.5 3.2c0-.4.3-.7.7-.7h11.6c.4 0 .7.3.7.7s-.3.7-.7.7H2.2c-.4 0-.7-.3-.7-.7Zm1.6 4.3c0-.4.3-.7.7-.7h8.4c.4 0 .7.3.7.7s-.3.7-.7.7H3.8c-.4 0-.7-.3-.7-.7Zm1.8 3.6c-.4 0-.7.3-.7.7s.3.7.7.7h4.8c.4 0 .7-.3.7-.7s-.3-.7-.7-.7H4.9Z"
							fill="#48566A"
						/>
					</svg>
					Bộ lọc
				</button>
			</div>
		</div>

		<div v-if="isList" class="assessment-hub__tabs">
			<button
				v-for="tab in pageTabs"
				:key="tab.id"
				type="button"
				class="assessment-hub__tab"
				:class="{ 'assessment-hub__tab--active': activeTab === tab.id }"
				@click="selectTab(tab.id)"
			>
				{{ tab.label }}
			</button>
		</div>

		<div v-if="isList && listLoading" class="flex justify-center py-24">
			<Loading />
		</div>

		<div
			v-else-if="isList && visibleItems.length"
			class="mt-8 overflow-x-auto"
		>
			<table class="process-table">
				<thead>
					<tr>
						<th>STT</th>
						<th>Học sinh</th>
						<th>Tiêu đề</th>
						<th>Trạng thái</th>
						<th>Ngày thực hiện</th>
						<th>Tuổi thực</th>
						<th v-if="isDevelopment">Tuổi phát triển</th>
						<th>Kết luận</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="(item, index) in visibleItems"
						:key="item.id"
						class="cursor-pointer"
						@click="openReport(item)"
					>
						<td>{{ index + 1 }}</td>
						<td>
							<div class="flex items-center gap-3 whitespace-nowrap">
								<img
									v-if="item.student?.profile_photo?.w200"
									:src="item.student.profile_photo.w200"
									alt=""
									class="h-6 w-6 rounded-full object-cover"
								/>
								<span
									v-else
									class="inline-flex items-center justify-center h-6 w-6 rounded-full text-white text-xs font-medium"
									:style="{
										background:
											item.student?.profile_photo?.default
												?.color || '#F1A267',
									}"
								>
									{{
										item.student?.profile_photo?.default
											?.initial
									}}
								</span>
								<span
									class="font-semibold"
									:style="{
										color:
											item.student?.profile_photo?.default
												?.color || '#F1A267',
									}"
								>
									{{ item.student?.name || '-' }}
								</span>
							</div>
						</td>
						<td>{{ item.title || '-' }}</td>
						<td v-if="isDevelopment">
							<span
								v-if="item.development_age && item.state !== 'completed'"
								class="form-goal"
							>
								<svg
									width="16"
									height="16"
									viewBox="0 0 16 16"
									fill="none"
								>
									<circle cx="8" cy="8" r="8" fill="#2AA797" />
									<path
										d="M4.5 8.2l2.2 2.2 4.8-5"
										stroke="#fff"
										stroke-width="1.6"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
								</svg>
								Kết quả MT
							</span>
							<span v-else-if="item.state === 'completed'">Hoàn thành</span>
							<span v-else>Bản nháp</span>
						</td>
						<td v-else>
							<button
								v-if="!item.sent_at"
								type="button"
								class="process-send"
								@click.stop="askSend(item)"
							>
								Gửi PH
							</button>
							<span v-else>Đã gửi</span>
						</td>
						<td class="whitespace-nowrap">
							{{ formatDate(item.date) || '-' }}
						</td>
						<td class="whitespace-nowrap">{{ showDash(item.age) }}</td>
						<td v-if="isDevelopment">
							{{ showDash(item.development_age) }}
						</td>
						<td>
							<div
								v-for="(line, lineIndex) in conclusionLines(item)"
								:key="lineIndex"
							>
								{{ line }}
							</div>
							<span v-if="!conclusionLines(item).length">-</span>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<div v-else-if="isList" class="flex flex-col items-center pt-16">
			<img
				src="@/assets/images/illustrations/screening-empty.svg"
				alt=""
				class="assessment-hub__art"
			/>
			<div class="assessment-hub__copy">
				<p>Chưa có bản đánh giá trong mục này</p>
			</div>
		</div>

		<div v-else class="flex flex-col items-center pt-16">
			<img
				src="@/assets/images/illustrations/screening-empty.svg"
				alt=""
				class="assessment-hub__art"
			/>
			<div class="assessment-hub__copy">
				<p>{{ route.meta.emptyHeading }}</p>
				<p class="mt-6">{{ route.meta.emptyBody }}</p>
			</div>
			<button
				type="button"
				class="button-p btn--ghost w-fit px-5 mt-10"
				@click="openCreate"
			>
				Tạo mới
			</button>
		</div>

		<DaModalFrame v-model:open="formOpen">
			<CreateNewForm
				v-if="formStudent"
				:student-id="formStudent.id"
				:student-name="formStudent.name"
				@close="formOpen = false"
			/>
		</DaModalFrame>
		<DaModalFrame v-model:open="sendOpen">
			<ConfirmSend @close="sendOpen = false" @submit="sendReport" />
		</DaModalFrame>
		<ScreeningCreate
			v-if="createOpen"
			student-only
			@close="createOpen = false"
			@continue="continueProcess"
		/>
		<ScreeningFilter
			v-if="filterOpen"
			:student-id="studentId"
			:state="stateFilter"
			@close="filterOpen = false"
			@apply="applyFilter"
		/>
	</div>
</template>

<style lang="scss" scoped>
.assessment-hub__icon {
	width: 28px;
	height: 28px;
	color: #65758b;
}

.assessment-hub__title {
	font-size: 28px;
	line-height: 32px;
	letter-spacing: -0.56px;
	font-weight: 700;
	color: #65758b;
}

.assessment-hub__filter {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 12px 20px;
	border-radius: 4px;
	background: #fff;
	color: #48566a;
	font-size: 14px;
	font-weight: 600;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px rgba(50, 50, 71, 0.05);
}

.assessment-hub__art {
	width: 250px;
	height: 250px;
	object-fit: contain;
	transform: rotate(180deg) scaleX(-1);
}

.assessment-hub__copy {
	width: 640px;
	max-width: 100%;
	margin-top: 24px;
	text-align: center;
	font-size: 18px;
	line-height: 24px;
	color: #94a3b8;
}

.assessment-hub__tabs {
	display: flex;
	gap: 32px;
	margin-top: 28px;
	border-bottom: 1px solid #e1e7ef;
}

.assessment-hub__tab {
	padding: 0 4px 12px;
	font-size: 14px;
	line-height: 20px;
	color: #94a3b8;
	border-bottom: 2px solid transparent;
	margin-bottom: -1px;
}

.assessment-hub__tab--active {
	color: #d89210;
	font-weight: 600;
	border-bottom-color: #f0bb4f;
}

.process-table {
	width: 100%;
	min-width: 960px;
	border-collapse: separate;
	border-spacing: 0;
	background: #fff;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24),
		0 3px 8px rgba(50, 50, 71, 0.05);

	th {
		background: #f1f5f9;
		padding: 24px 16px;
		text-align: left;
		font-size: 12px;
		line-height: 16px;
		font-weight: 600;
		letter-spacing: 0.96px;
		text-transform: uppercase;
		color: #48566a;
		white-space: nowrap;
	}

	td {
		padding: 16px;
		font-size: 16px;
		line-height: 24px;
		color: #344256;
		background: #fff;
		border-top: 1px solid #edf2f7;
		vertical-align: middle;
	}
}

.process-send {
	padding: 8px 12px;
	border-radius: 4px;
	border: 1px solid #d89210;
	color: #d89210;
	font-size: 12px;
	line-height: 16px;
	font-weight: 600;
	background: #fff;
}

.form-goal {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	white-space: nowrap;
}
</style>
